import { useRef, useState } from "react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { OrderLineTypes, Status } from "@repo/database";
import { formatIntToCurrency, generateRandomString } from "@repo/utils";

import type { CartItem } from "~/providers/cart-provider";
import type { Product } from "~/providers/product-provider";
import { useConfiguration } from "~/hooks/use-configuration";
import { useCourse } from "~/hooks/use-course";
import { useGeneral } from "~/hooks/use-general";
import { useProduct } from "~/hooks/use-product";
import { useTable } from "~/hooks/use-table";
import { useOrderStore } from "~/stores/use-order-store";
import { usePermissionStore } from "~/stores/use-permission-store";
import { useTableStore } from "~/stores/use-table-store";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { MemoizedKeypadInput } from "../ui/keypad-input";
import { RegisterButton } from "../ui/register-button";

export function ProductButton({
  product,
  onClick,
}: {
  product: Product;
  onClick?: (item: CartItem) => void;
}) {
  const { currentOrder } = useOrderStore();
  const [isLongPress, setIsLongPress] = useState(false);

  const [contextOpen, setContextOpen] = useState(false);

  const longPressTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const startLongPress = () => {
    setIsLongPress(true);
    setContextOpen(true);
  };

  const handleAdd = (amount: number) => {
    setContextOpen(false);
    if (onClick) {
      for (let i = 0; i < amount; i++) {
        onClick({
          ordered: false,
          printed: false,
          reference: generateRandomString(8),
          name: product.name,
          preparationName: product.preparationName,
          value: price,
          quantity: 1,
          originalQuantity: 1,
          vatRate: selectedVatRate?.value ?? 0,
          vatRateName: selectedVatRate?.name,
          categoryName: category?.name ?? "Uncategorized",
          printRouteId,
          notifyPrintRouteId,
          turnoverGroupName:
            product.turnoverGroupName ?? category?.turnoverGroupName,
          turnoverGroupLedgerNumber:
            product.turnoverGroupLedgerNumber ??
            category?.turnoverGroupLedgerNumber,
          createdAt: new Date(),
          courseId: product.courseId,
          courseName: courses.find((course) => course.id === product.courseId)
            ?.name,
          productId: product.id,
          categoryId: category?.id,
          employeeId: currentEmployee?.id,
          type: OrderLineTypes.product,
          orderId: currentOrder?.id,
        });
      }
    }
  };

  const handleMouseDown = () => {
    setIsLongPress(false);
    longPressTimeoutRef.current = setTimeout(() => {
      startLongPress();
    }, 1000);
  };

  const handlePressEnd = () => {
    if (longPressTimeoutRef.current) {
      clearTimeout(longPressTimeoutRef.current); // Cancel long press if regular press
    }
  };

  const handleClick = () => {
    handleAdd(1);
  };

  const handleMouseUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    handlePressEnd();

    if (!isLongPress) {
      // Handle regular press if it wasn't a long press
      if (e.button !== 0) return;
      handleClick();
    }
  };
  const { productCategories } = useProduct();

  const linkedCategory = productCategories.find(
    (productCategory) => productCategory.id === product.categoryId,
  );

  const { currentEmployee } = usePermissionStore();

  const color = product.color ?? linkedCategory?.color;

  const { tables } = useTable();

  const { vatRates } = useGeneral();

  const { courses } = useCourse();

  const { currentTable } = useTableStore();

  const { cashRegisterConfiguration } = useConfiguration();

  const tablePriceId = tables.find(
    (table) => table.id === currentTable?.id,
  )?.priceId;

  const tablePrice = product.prices.find(
    (price) => price.priceId === tablePriceId,
  );

  const defaultPrice = product.prices.find(
    (price) => price.priceId === cashRegisterConfiguration.defaultPriceId,
  );

  const price = tablePrice
    ? tablePrice.value
    : defaultPrice
      ? defaultPrice.value
      : 0;

  const category = productCategories.find(
    (productCategory) => productCategory.id === linkedCategory?.id,
  );

  const printRouteId = product.printRouteId ?? category?.printRouteId;

  const notifyPrintRouteId =
    product.notifyPrintRouteId ?? category?.notifyPrintRouteId;

  const selectedVatRate = vatRates.find(
    (vatRate) => vatRate.id === product.vatRateId,
  );

  return (
    <>
      <RegisterButton
        onPointerDown={handleMouseDown}
        onPointerUp={handleMouseUp}
        onPointerLeave={handlePressEnd}
        onContextMenu={(e) => {
          e.preventDefault();
          setContextOpen(true);
        }}
        className="min-h-14 max-h-14 justify-center p-1 overflow-hidden"
        color={color ?? undefined}
        disabled={product.status === Status.inactive}
      >
        <p className="line-clamp-2 w-full truncate text-ellipsis whitespace-normal break-words uppercase">
          {product.name}
        </p>

        <p className="w-full text-right text-2xs absolute right-1 bottom-0">
          {formatIntToCurrency(price, false)}
        </p>
      </RegisterButton>
      <Dialog open={contextOpen} onOpenChange={setContextOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enter amount to add</DialogTitle>
            <VisuallyHidden>
              <DialogDescription></DialogDescription>
            </VisuallyHidden>
          </DialogHeader>
          <MemoizedKeypadInput
            type="number"
            value={1}
            min={1}
            max={100}
            onChange={handleAdd}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
